import React, { useEffect } from "react";

import DatatablePage from "../../../layout/datatable-pages/companies";
import { fetchData, getCompaniesFieldsArray } from "../../../utils/helpers";
import { props } from "../../datatable/props";
import useDatatable from "../../hooks/useDatatable";

import "../datatable-pages.css";
import { useSupabaseApp } from "../../../SupabaseApp";

/**
 * ShutteredCompanies Component
 * 
 * Displays a paginated datatable of inactive/shuttered companies with comprehensive business history.
 * Users can search through 35+ company attributes including financial data, failure reasons,
 * management, investors, and operational timeline. A sticky column displays 'reasonsForFailure'
 * to keep shutdown reasons visible while scrolling. Shows statistics on inactive companies
 * and reasons for shutdown. Requires user authentication.
 * 
 * @returns {JSX.Element} Datatable page with shuttered companies data
 */
const ShutteredCompanies = () => {
  // Array of field keys for inactive company datatable
  const fieldKeys = [
    "tradeName",
    "entityType",
    "description",
    "stageOfDevelopment",
    "reasonsForFailure",
    "categories",
    "industry",
    "location",
    "ownership",
    "businessModelType",
    "defensibilityMoat",
    "lastFinancingRound",
    "totalInvestment",
    "postMoneyValuation",
    "lastFinancingDate",
    "numOfFundingRounds",
    "numOfInvestors",
    "investors",
    "revenue",
    "market",
    "investments",
    "numOfAcquisitions",
    "boardSeats",
    "competitors",
    "similarEntities",
    "management",
    "numOfEmployees",
    "reputation",
    "phoneNumber",
    "operationalDuration",
    "shutdownDate",
    "foundedDate",
    "legalName",
    "former_legalName",
    "ticker",
  ];

  // Column that remains visible during horizontal scrolling for reference
  const stickyField = "reasonsForFailure";

  // Convert field keys to formatted field objects with sticky column preference
  const fields = getCompaniesFieldsArray(fieldKeys, stickyField);

  // Get datatable state and props
  const { datatableProps } = useDatatable();

  // Get Supabase utilities and user context
  const { currentUser, supabase, getUserTier } = useSupabaseApp();

  // Verify user authentication before rendering
  if (!currentUser) {
    throw new Error(`Must be logged in`);
  }

  // Configuration for shuttered companies datatable
  const params = {
    ...datatableProps,
    supabase,
    getData: "getShutteredCompanies",
    collection: "companies",
    title: "Shuttered companies",
    datatipText:
      "Discern meaningful insights from our collection of shuttered companies.",
    statsArray: [
      {
        actualName: "inactiveCompanies",
        displayName: "Inactive companies",
        total: "10,000",
      },
      {
        actualName: "reasonsForShutdown",
        displayName: "Reasons for shutdown",
        total: "970",
      },
    ],
    placeholderText: "Search from shuttered companies",
    fields: fields,
    getCompanyDetailsForExpand: "getCompanyDetailsForExpand",
  };

  // Fetch companies data on component mount
  useEffect(() => {
    fetchData(params);
  }, []);

  // Render datatable page with configured params
  return <DatatablePage props={props(params)} />;
};

export default ShutteredCompanies;
